import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { ApiError, formatRange, useSnackbar } from '@jupi/ui'
import { useReleaseBooking, useReleaseRun, type FirePatrolBooking } from './api'

// A dispatcher releasing the aircraft: this one day, or the whole run that was
// booked in one go (including days outside the week on screen). Whoever the
// patrol had displaced gets their booking back, and is told.
export default function ReleaseBookingDialog({ booking, onClose }: { booking: FirePatrolBooking; onClose: () => void }) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const releaseDay = useReleaseBooking()
  const releaseRun = useReleaseRun()
  const [reason, setReason] = useState('')
  const busy = releaseDay.isPending || releaseRun.isPending

  const done = async (run: boolean) => {
    try {
      const result = run
        ? await releaseRun.mutateAsync({ bulkGroupId: booking.bulkGroupId!, reason: reason || undefined })
        : await releaseDay.mutateAsync({ bookingId: booking.bookingId, reason: reason || undefined })
      showSnackbar(t('firePatrol.releaseBooking.done', { count: result.cancelled.length }))
      if (result.promoted.length > 0) {
        showSnackbar(t('firePatrol.releaseBooking.restored', { count: result.promoted.length }), { severity: 'info' })
      }
      onClose()
    } catch (err) {
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('firePatrol.releaseBooking.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DialogContentText>
            {t('firePatrol.releaseBooking.intro', {
              aircraft: booking.registration,
              when: formatRange(booking.startsAt, booking.endsAt),
            })}
          </DialogContentText>
          <DialogContentText variant="body2">{t('firePatrol.releaseBooking.effect')}</DialogContentText>
          <TextField
            size="small"
            multiline
            minRows={2}
            label={t('firePatrol.releaseBooking.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>{t('firePatrol.close')}</Button>
        {booking.bulkGroupId ? (
          <Button color="error" startIcon={<Icon icon="mdi:calendar-remove" />} disabled={busy} onClick={() => done(true)}>
            {t('firePatrol.releaseBooking.run')}
          </Button>
        ) : null}
        <Button variant="contained" color="error" startIcon={<Icon icon="mdi:delete" />} disabled={busy} onClick={() => done(false)}>
          {t('firePatrol.releaseBooking.day')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
